import React, { useState, useEffect } from "react";
import { X, Save, Loader2, Building2, Globe, Mail, Phone, User, Briefcase, ChevronRight } from "lucide-react";
import { clientsAPI, employeesAPI } from "../../../services/api";
import { toast } from "react-toastify";

const SECTIONS = [
  { id: "basic", label: "Basic Info" },
  { id: "contact", label: "Contact" },
  { id: "address", label: "Address" },
  { id: "financial", label: "Financial" },
  { id: "client", label: "Client Info" },
  { id: "preferences", label: "Preferences" },
];

const FIELD_CONFIG = {
  basic: [
    { name: "companyName", label: "Company Name", type: "text", required: true },
    { name: "websiteUrl", label: "Website URL", type: "url" },
    { name: "linkedInUrl", label: "LinkedIn URL", type: "url" },
    {
      name: "industry", label: "Industry", type: "select",
      options: ["IT", "FINANCE", "HEALTHCARE", "EDUCATION", "MANUFACTURING", "RETAIL", "OTHER"]
    },
    {
      name: "relationshipType", label: "Relationship Type", type: "select",
      options: ["CLIENT", "VENDOR", "PARTNER", "DISTRIBUTOR", "RESELLER", "CONTRACTOR", "CONSULTANT", "SERVICE_PROVIDER", "INVESTOR", "MANUFACTURER", "SUPPLIER", "OTHER"]
    },
    {
      name: "leadStatus", label: "Lead Status", type: "select",
      options: ["NEW", "CONTACTED", "QUALIFIED", "LOST", "WON", "OTHER"]
    },
    {
      name: "leadSource", label: "Lead Source", type: "select",
      options: ["WEBSITE", "REFERRAL", "PARTNER", "SOCIAL_MEDIA", "EMAIL", "CALL", "OTHER"]
    },
    {
      name: "companyStage", label: "Company Stage", type: "select",
      options: ["STARTUP", "SMALL", "MEDIUM", "LARGE", "ENTERPRISE", "OTHER"]
    },
    {
      name: "legalType", label: "Legal Type", type: "select",
      options: ["SOLE_PROPRIETORSHIP", "PARTNERSHIP_FIRM", "LLP", "PRIVATE_LIMITED", "PUBLIC_LIMITED", "OPC", "JOINT_VENTURE", "NON_PROFIT", "GOVERNMENT_ENTITY", "OTHER"]
    },
    {
      name: "employeeCount", label: "Employee Count", type: "select",
      options: ["RANGE_1_10", "RANGE_11_50", "RANGE_51_100", "RANGE_101_250", "RANGE_251_500", "RANGE_501_1000", "RANGE_1001_5000", "RANGE_5001_10000", "RANGE_10000_PLUS"]
    },
    {
      name: "annualRevenue", label: "Annual Revenue", type: "select",
      options: ["BELOW_1M", "RANGE_1M_5M", "RANGE_5M_10M", "RANGE_10M_50M", "RANGE_50M_100M", "RANGE_100M_500M", "RANGE_500M_1B", "RANGE_1B_5B", "RANGE_5B_10B", "ABOVE_10B"]
    },
  ],
  contact: [
    { name: "primaryContact", label: "Primary Contact Name", type: "text" },
    { name: "jobTitle", label: "Job Title", type: "text" },
    { name: "email", label: "Email", type: "email" },
    { name: "phone", label: "Phone", type: "tel" },
    { name: "contactOwner", label: "Contact Owner", type: "employee-select" },
  ],
  // ✅ Address fields use nested path format: "billingAddress.streetLine1"
  // These match the backend Client.Address fields exactly
  address: {
    billing: [
      { name: "billingAddress.streetLine1", label: "Street Line 1", type: "text" },
      { name: "billingAddress.streetLine2", label: "Street Line 2", type: "text" },
      { name: "billingAddress.city",        label: "City",          type: "text" },
      { name: "billingAddress.state",       label: "State",         type: "text" },
      { name: "billingAddress.country",     label: "Country",       type: "text" },
      { name: "billingAddress.zipcode",     label: "Zipcode",       type: "text" },
    ],
    shipping: [
      { name: "shippingAddress.streetLine1", label: "Street Line 1", type: "text" },
      { name: "shippingAddress.streetLine2", label: "Street Line 2", type: "text" },
      { name: "shippingAddress.city",        label: "City",          type: "text" },
      { name: "shippingAddress.state",       label: "State",         type: "text" },
      { name: "shippingAddress.country",     label: "Country",       type: "text" },
      { name: "shippingAddress.zipcode",     label: "Zipcode",       type: "text" },
    ],
  },
  financial: [
    { name: "taxIdType",   label: "Tax ID Type",   type: "text" },
    { name: "taxIdNumber", label: "Tax ID Number", type: "text" },
    {
      name: "paymentTerms", label: "Payment Terms", type: "select",
      options: ["NET15", "NET30", "NET45", "NET60", "NET90", "COD", "PREPAID", "LC", "PAYMENT_ON_RECEIPT", "MONTHLY", "QUARTERLY", "ANNUAL", "OTHER"]
    },
    {
      name: "currency", label: "Currency", type: "select",
      options: ["INR", "USD", "EUR", "GBP", "AUD"]
    },
  ],
  client: [
    { name: "hiringManager", label: "Hiring Manager", type: "text" },
    { name: "pocName",       label: "POC Name",       type: "text" },
    { name: "pocJobTitle",   label: "POC Job Title",  type: "text" },
    { name: "pocEmail",      label: "POC Email",      type: "email" },
    { name: "pocPhone",      label: "POC Phone",      type: "tel" },
  ],
  preferences: [
    { name: "doNotCall",  label: "Do Not Call",  type: "checkbox" },
    { name: "doNotEmail", label: "Do Not Email", type: "checkbox" },
  ],
};

// ✅ Helper: get nested value from formData using dot-path e.g. "billingAddress.city"
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, key) => acc?.[key], obj) ?? "";
};

// ✅ Helper: set nested value immutably using dot-path
const setNestedValue = (obj, path, value) => {
  const keys = path.split(".");
  const updated = { ...obj };
  let ref = updated;
  for (let i = 0; i < keys.length - 1; i++) {
    ref[keys[i]] = { ...(ref[keys[i]] || {}) };
    ref = ref[keys[i]];
  }
  ref[keys[keys.length - 1]] = value;
  return updated;
};

const ClientEditDrawer = ({ client, onClose, onSave }) => {
  const [activeSection, setActiveSection] = useState("basic");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [employees, setEmployees] = useState([]);
  const [employeeNameMap, setEmployeeNameMap] = useState({});
  const [sameAsBilling, setSameAsBilling] = useState(false);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await employeesAPI.getAllEmployees();
        const empList = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setEmployees(empList);
        const nameMap = {};
        empList.forEach(emp => {
          nameMap[emp.id] = emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || emp.id;
        });
        setEmployeeNameMap(nameMap);
      } catch (error) {
        console.error('Failed to fetch employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (client) {
      setFormData({ ...client });
      setSameAsBilling(!!client.sameAsBilling);
    }
  }, [client]);

  // ✅ handleChange supports both flat fields and nested dot-path fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTouched(true);
    const newValue = type === "checkbox" ? checked : value;

    if (name.includes(".")) {
      // Nested field e.g. "billingAddress.city"
      setFormData(prev => setNestedValue(prev, name, newValue));
    } else {
      setFormData(prev => ({ ...prev, [name]: newValue }));
    }

    if (errors[name]) setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
  };

  // ✅ Copy billing address to shipping address
  const handleSameAsBilling = (checked) => {
    setSameAsBilling(checked);
    setTouched(true);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        sameAsBilling: true,
        shippingAddress: { ...(prev.billingAddress || {}) },
      }));
    } else {
      setFormData(prev => ({ ...prev, sameAsBilling: false }));
    }
  };

  const handleSave = async () => {
    const errs = {};
    if (!formData.companyName?.trim()) errs.companyName = "Company Name is required";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) errs.email = "Invalid email format";
    if (formData.pocEmail && !emailRegex.test(formData.pocEmail)) errs.pocEmail = "Invalid POC email format";

    const validatePhone = (val) => {
      const digits = String(val || '').replace(/\D/g, '');
      return !val || digits.length >= 10;
    };
    if (!validatePhone(formData.phone)) errs.phone = "Invalid 10-digit phone number";
    if (!validatePhone(formData.pocPhone)) errs.pocPhone = "Invalid 10-digit POC phone number";

    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Please fix the errors before saving");
      const firstErrField = Object.keys(errs)[0];
      for (const sectionId in FIELD_CONFIG) {
        const fields = Array.isArray(FIELD_CONFIG[sectionId])
          ? FIELD_CONFIG[sectionId]
          : [
              ...(FIELD_CONFIG[sectionId]?.billing || []),
              ...(FIELD_CONFIG[sectionId]?.shipping || []),
            ];
        if (fields.some(f => f.name === firstErrField)) {
          setActiveSection(sectionId);
          break;
        }
      }
      return;
    }

    setLoading(true);
    try {
      await clientsAPI.updateClient(client.id, formData);
      toast.success("Client updated successfully");
      onSave?.(formData);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update client");
    } finally {
      setLoading(false);
    }
  };

  const currentFields = Array.isArray(FIELD_CONFIG[activeSection])
    ? FIELD_CONFIG[activeSection]
    : [];

  const sectionIcons = {
    basic:       Building2,
    contact:     User,
    address:     Globe,
    financial:   Briefcase,
    client:      User,
    preferences: Mail,
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] z-40" onClick={onClose} />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col"
        style={{ animation: "slideIn 0.25s ease-out" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <Building2 size={18} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 leading-tight">Edit Client</h2>
              <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[220px]">{client?.companyName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {touched && (
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-gray-100 bg-gray-50 overflow-x-auto flex-shrink-0">
          {SECTIONS.map((section) => {
            const Icon = sectionIcons[section.id];
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-all ${
                  isActive
                    ? "border-blue-500 text-blue-600 bg-white"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-white"
                }`}
              >
                {Icon && <Icon size={13} />}
                {section.label}
              </button>
            );
          })}
        </div>

        {/* Form Fields */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="space-y-4">

            {/* ✅ Address section — nested fields with billingAddress.* / shippingAddress.* */}
            {activeSection === "address" && (
              <>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
                  Billing Address
                </p>
                {FIELD_CONFIG.address.billing.map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    value={getNestedValue(formData, field.name)}
                    error={errors[field.name]}
                    onChange={handleChange}
                    employees={employees}
                    employeeNameMap={employeeNameMap}
                  />
                ))}

                {/* Same as Billing toggle */}
                <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={sameAsBilling}
                    onChange={(e) => handleSameAsBilling(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-xs font-medium text-gray-600">
                    Shipping address same as billing
                  </span>
                </label>

                {!sameAsBilling && (
                  <>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-4 mb-1">
                      Shipping Address
                    </p>
                    {FIELD_CONFIG.address.shipping.map((field) => (
                      <FormField
                        key={field.name}
                        field={field}
                        value={getNestedValue(formData, field.name)}
                        error={errors[field.name]}
                        onChange={handleChange}
                        employees={employees}
                        employeeNameMap={employeeNameMap}
                      />
                    ))}
                  </>
                )}
              </>
            )}

            {activeSection === "client" && (
              <>
                {FIELD_CONFIG.client.slice(0, 1).map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    value={formData[field.name]}
                    error={errors[field.name]}
                    onChange={handleChange}
                    employees={employees}
                    employeeNameMap={employeeNameMap}
                  />
                ))}
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-4 mb-1">
                  Point of Contact
                </p>
                {FIELD_CONFIG.client.slice(1).map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    value={formData[field.name]}
                    error={errors[field.name]}
                    onChange={handleChange}
                    employees={employees}
                    employeeNameMap={employeeNameMap}
                  />
                ))}
              </>
            )}

            {activeSection !== "address" && activeSection !== "client" &&
              currentFields.map((field) => (
                <FormField
                  key={field.name}
                  field={field}
                  value={formData[field.name]}
                  error={errors[field.name]}
                  onChange={handleChange}
                  employees={employees}
                  employeeNameMap={employeeNameMap}
                />
              ))
            }
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
          >
            {loading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={15} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
};

const FormField = ({ field, value, error, onChange, employees, employeeNameMap }) => {
  const baseInput = `w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${
    error ? "border-red-500 bg-red-50" : "border-gray-200 bg-white"
  }`;

  if (field.type === "checkbox") {
    return (
      <div className="space-y-1">
        <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors">
          <input
            type="checkbox"
            name={field.name}
            checked={!!value}
            onChange={onChange}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 font-medium">{field.label}</span>
        </label>
        {error && <p className="text-red-600 text-[10px] ml-1">⚠ {error}</p>}
      </div>
    );
  }

  if (field.type === "employee-select") {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <select
          name={field.name}
          value={value || ""}
          onChange={onChange}
          className={baseInput}
        >
          <option value="">Select {field.label}</option>
          {employees.map((emp) => (
            <option key={emp.id} value={emp.id}>
              {employeeNameMap[emp.id] || emp.name || emp.email}
            </option>
          ))}
        </select>
        {error && <p className="text-red-600 text-[10px] mt-1">⚠ {error}</p>}
        {value && employeeNameMap[value] && !error && (
          <p className="text-xs text-gray-400 mt-1">Selected: {employeeNameMap[value]}</p>
        )}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <select
          name={field.name}
          value={value || ""}
          onChange={onChange}
          className={baseInput}
        >
          <option value="">Select {field.label}</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </option>
          ))}
        </select>
        {error && <p className="text-red-600 text-[10px] mt-1">⚠ {error}</p>}
      </div>
    );
  }

  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1.5">
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={field.type}
        name={field.name}
        value={value || ""}
        onChange={onChange}
        className={baseInput}
        placeholder={`Enter ${field.label.toLowerCase()}`}
      />
      {error && <p className="text-red-600 text-[10px] mt-1">⚠ {error}</p>}
    </div>
  );
};

export default ClientEditDrawer;